import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {
  computeIsConfigTable,
  computeTableProvenanceUuid,
} from 'notebook/tables/store/logic/TableContextLogic';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * What the table is and where it lives: its identifiers, display name, kind, and config mode. No
 * input reads the cell data, so cell edits leave this slice referentially unchanged.
 */
export interface TableContextModel {
  isConfigTable: boolean;
  tableProvenanceUuid: string | null;

  /** The table's unique name within its document. */
  name: string;

  /** A custom rendering of the table name, shown in place of `name` when present. */
  renderedName: JSX.Element | string | undefined;

  /** The table's API id, or undefined for tables that have not been saved. */
  apiId: string | undefined;

  /** The id of the file that contains the table. */
  fileId: string | undefined;

  /** The table component's client id, used to build cell cids and DOM handles. */
  cid: string;

  /** The table's type (`table._tableType`), or null when unset. */
  tableType: string | null;

  /** The structured table kind, or null when the table is not a structured table. */
  tableKind: StructuredTableKind | null;
}

export function createTableContextSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps
): (store: TableEditableStore) => TableContextModel {
  return createTableStoreSelector(
    'tableContext',
    {
      metadata: (store) => store.volatileProps.table.metadata,
      editorContextIsConfig: (store) => store.editorContext.isConfig,
      fileIsConfigProp: (store) => store.stableProps.fileIsConfig,
      tableProvenanceUuid: (store) => volatilePropsSelector(store).table.provenanceUuid,
      proxyProvenanceUuid: (store) => store.stableProps.proxyProvenanceUuid,
      name: (store) => store.volatileProps.table.name,
      renderedName: (store) => store.volatileProps.table.renderedName,
      apiId: (store) => store.volatileProps.table.apiId,
      fileId: (store) => store.volatileProps.table.fileId,
      cid: (store) => store.stableProps.cid,
      tableType: (store) => store.volatileProps.table._tableType ?? null,
      tableKind: (store) => TableUtil.getTableKind(store.volatileProps.table),
    },
    ({
      metadata,
      editorContextIsConfig,
      fileIsConfigProp,
      tableProvenanceUuid,
      proxyProvenanceUuid,
      name,
      renderedName,
      apiId,
      fileId,
      cid,
      tableType,
      tableKind,
    }) => ({
      isConfigTable: computeIsConfigTable(
        {metadata, _tableType: tableType, fileId},
        editorContextIsConfig,
        fileIsConfigProp
      ),
      tableProvenanceUuid: computeTableProvenanceUuid(tableProvenanceUuid, proxyProvenanceUuid),
      name,
      renderedName,
      apiId,
      fileId,
      cid,
      tableType,
      tableKind,
    })
  );
}
